/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { debounce as _debounce } from 'lodash';
import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo } from 'react';
import Cropper, { CropperProps } from 'react-easy-crop';

import { Mark } from '@mui/base';
import { Slider } from '@mui/material';

import { PHOTO_ACCEPTABLE_FILES } from '../../constants/settings';
import { ConvertibleCanvas } from '../../miscellaneous/ConvertibleCanvas';
import { getCroppedImg } from '../../utils/canvasUtils';
import { useObjectState } from '../../utils/useObjectState';
import { useEventCallback } from '../hooks/useEventCallback';
import { useEditPhotoStyles } from './EditPhoto.styles';

export type EditPhotoProps = {
  imageSrc: string;
  aspect?: number;
  onChange?: (canvas: ConvertibleCanvas) => any;
};
type EditPhotoState = {
  crop: { x: number; y: number };
  rotation: number;
  zoom: number;
  croppedAreaPixels: NonNullable<CropperProps['initialCroppedAreaPixels']> | null;
};
export type EditPhotoHelpers = {
  getNewImage: () => Promise<ConvertibleCanvas | undefined>;
};

const ZOOM_MIN = 1;
const ZOOM_MAX = 3;
export const EDIT_PHOTO_ACCEPTABLE_FILES = PHOTO_ACCEPTABLE_FILES;

export const EditPhoto = memo(
  forwardRef<EditPhotoHelpers, EditPhotoProps>((props, ref) => {
    const { imageSrc, aspect = 1, onChange } = props;
    const [state, setState] = useObjectState<EditPhotoState>({
      zoom: 1,
      rotation: 0,
      crop: { x: 0, y: 0 },
      croppedAreaPixels: null,
    });
    const { crop, rotation, zoom, croppedAreaPixels } = state;
    const classes = useEditPhotoStyles();

    const zoomSliderMarks = useMemo(() => {
      const result: Mark[] = [];
      for (let i = ZOOM_MIN; i <= ZOOM_MAX; i += 0.5) {
        result.push({ value: +i.toFixed(1), label: i.toFixed(1) });
      }
      return result;
    }, []);

    const rotateSliderMarks = useMemo(() => {
      const result: Mark[] = [];
      for (let i = 0; i <= 360; i += 90) {
        result.push({ value: i, label: i });
      }
      return result;
    }, []);

    const setCrop = useCallback(
      (crop: EditPhotoState['crop']) => {
        setState({ crop });
      },
      [setState],
    );
    const setRotation = useCallback(
      (rotation: EditPhotoState['rotation']) => {
        setState({ rotation });
      },
      [setState],
    );
    const setZoom = useCallback(
      (zoom: EditPhotoState['zoom']) => {
        setState({ zoom });
      },
      [setState],
    );
    const setCroppedAreaPixels = useCallback(
      (croppedAreaPixels: NonNullable<CropperProps['initialCroppedAreaPixels']>) => {
        setState({ croppedAreaPixels });
      },
      [setState],
    );

    const getNewImage = useEventCallback(async () => {
      try {
        return await getCroppedImg(imageSrc!, croppedAreaPixels!, rotation);
      } catch (e) {
        console.error(e);
      }
    });

    const onCropComplete = useCallback(
      (
        _croppedArea: NonNullable<CropperProps['initialCroppedAreaPixels']>,
        croppedAreaPixels: NonNullable<CropperProps['initialCroppedAreaPixels']>,
      ) => {
        setCroppedAreaPixels(croppedAreaPixels);
      },
      [setCroppedAreaPixels],
    );

    const hasOnChangeCallback = useMemo<boolean>(() => onChange !== undefined, [onChange]);

    const onChangeInner = useEventCallback(async () => {
      if (onChange && croppedAreaPixels) {
        try {
          const canvas = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
          onChange(canvas);
        } catch (e) {
          console.error(e);
        }
      }
    });

    const onChangeInnerDebounced = useMemo(() => _debounce(onChangeInner, 150, { maxWait: 2000 }), [onChangeInner]);

    useEffect(() => {
      if (hasOnChangeCallback) {
        void onChangeInnerDebounced();
      }
    }, [croppedAreaPixels, imageSrc, onChangeInnerDebounced, rotation, hasOnChangeCallback]);

    useImperativeHandle(
      ref,
      () => ({
        getNewImage,
      }),
      [getNewImage],
    );

    return (
      <div className={classes.root}>
        <div className={classes.cropContainer}>
          <Cropper
            image={imageSrc}
            crop={crop}
            rotation={rotation}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onRotationChange={setRotation}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            restrictPosition={true}
          />
        </div>
        <div className={classes.controls}>
          <div className={classes.sliderContainer}>
            <Slider
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e, zoom) => setZoom(zoom as number)}
              marks={zoomSliderMarks}
            />
          </div>
          <div className={classes.sliderContainer}>
            <Slider
              value={rotation}
              min={0}
              max={360}
              step={90}
              onChange={(e, rotation) => setRotation(rotation as number)}
              marks={rotateSliderMarks}
            />
          </div>
        </div>
      </div>
    );
  }),
);
